import { Injectable } from '@angular/core';
import { FaxService } from '../api/services/fax.service';
import { LookupModel } from '../api/models/lookup-model';
import { FaxTemplateService } from '../api/services/fax-template.service';
import { FaxTemplateModel } from '../api/models/fax-template-model';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { FaxesRequest } from '../api/models/faxes-request';
import { PhoneNumbersUtils } from '../core/utilities/phone-numbers-helpers';
import { RetryFaxModel } from '../api/models/retry-fax-model';
import { UpdateFaxNotesRequest } from '../api/models/update-fax-notes-request';
import { lastValueFrom } from 'rxjs';
import { BooleanResponse } from '../api/models';

@Injectable()
export class LocalFaxService {
  public listState: FaxesRequest = {
    skip: 0,
    limit: 100,
    startDate: '',
    endDate: '',
    faxId: undefined,
    sort: [{ field: 'faxDate', direction: 'DESC' }],
    user: null,
  };

  getAllNumbersInProgress = true;
  getOutgoingNumbersInProgress = true;
  outgoingFaxNumbers: Array<LookupModel> = [];
  allFaxNumbers: Array<LookupModel> = [];

  constructor(
    private apiFaxService: FaxService,
    private apiFaxTemplateService: FaxTemplateService,
    private notificationsService: LocalNotificationsService,
  ) {}

  public async markNotificationAsRead(nKey: string) {
    await this.notificationsService.markUnreadNotifications(0, nKey);
    return;
  }

  public async markNotificationAsUnRead(nKey: string) {
    await this.notificationsService.markUnreadNotifications(1, nKey);
    return;
  }

  public async deleteFax(callerId: string, id: string) {
    await lastValueFrom(
      this.apiFaxService.faxDeleteFax({
        callerId,
        id,
      }),
    );
  }

  hasAnyFaxNumbers(): boolean {
    return this.allFaxNumbers.length > 0;
  }

  hasOutgoingFaxNumbers(): boolean {
    return this.outgoingFaxNumbers.length > 0;
  }

  public async faxOutgoingNumbersList(): Promise<LookupModel[]> {
    this.getOutgoingNumbersInProgress = true;
    const response = await lastValueFrom(this.apiFaxService.faxGetMyOutgoingFaxNumbers());
    this.getOutgoingNumbersInProgress = false;

    this.outgoingFaxNumbers = response.map(item => {
      return {
        id: item.phoneNumber,
        text: PhoneNumbersUtils.format(item.phoneNumber),
        isSystem: false,
      };
    });

    return this.outgoingFaxNumbers;
  }

  public async faxAllNumbersList(): Promise<LookupModel[]> {
    this.getAllNumbersInProgress = true;
    const response = await lastValueFrom(this.apiFaxService.faxGetMyAllFaxNumbers());
    this.getAllNumbersInProgress = false;

    this.allFaxNumbers = response.map(item => {
      return {
        id: item.phoneNumber,
        text: PhoneNumbersUtils.format(item.phoneNumber),
        isSystem: false,
      };
    });

    return this.allFaxNumbers;
  }

  public async getFaxTemplates(): Promise<FaxTemplateModel[]> {
    const response = await lastValueFrom(this.apiFaxTemplateService.faxTemplateGetFaxTemplates());

    const item: FaxTemplateModel = {
      id: '-1',
      name: 'Use custom file',
      formFields: undefined,
      previewImage: undefined,
      pdfTemplate: undefined,
      isDefault: false,
    };

    response.splice(0, 0, item);

    return response;
  }

  public async sendFax(model: {
    numbersTo: string[];
    numberFrom: string;
    fromName: string;
    faxInternalNotes: string;
    files: File[];
    canMerge: boolean;
  }): Promise<BooleanResponse> {
    console.log('sendFax');
    try {
      return await lastValueFrom(
        this.apiFaxService.faxSendFax({
          body: {
            canMerge: model.canMerge,
            faxInternalNotes: model.faxInternalNotes,
            fromName: model.fromName,
            numberFrom: model.numberFrom,
            numbersTo: model.numbersTo,
            files: model.files,
          },
        }),
      );
      //await this.postFaxSendFax(model);
    } catch (e: any) {
      console.error(e);
      return { success: false, errorMessage: e?.error?.message };
    }
  }

  async retryFax(model: RetryFaxModel): Promise<BooleanResponse> {
    try {
      return await lastValueFrom(this.apiFaxService.faxRetryFax({ body: model }));
    } catch (e: any) {
      console.error(e);
      return { success: false, errorMessage: e?.error?.message };
    }
  }

  async saveInternalNotes(reqData: UpdateFaxNotesRequest) {
    try {
      await lastValueFrom(this.apiFaxService.faxUpdateFaxNotes({ body: reqData }));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}
